import { useEffect, useCallback, useState } from 'react'
import { Providers } from './providers'
import { Router } from './router'
import './scss/main.scss'
import {
    AUTHENTICATED_USER,
    AUTH_TOKEN,
    GUEST_USER,
} from './utils/constants/enum'
import { guestCheckInEDV } from './services/endeavor'
import { Axios } from './services/axios'
import GoogleTagManager from 'react-gtm-module'
import branch from 'branch-sdk'
import { clearCacheAndReload } from './utils/constants'

function App() {
    const [loadApp, setLoadApp] = useState(false)
    const reloadOnLogout = (e: StorageEvent) => {
        if (
            e.key === AUTHENTICATED_USER.IS_LOGIN_USER &&
            e.oldValue === AUTHENTICATED_USER.IS_AUTHENTICATED_USER &&
            e.newValue === null
        ) {
            window.location.reload()
        }
    }

    const guestCheckIn = useCallback(async () => {
        try {
            const isGuestUser = localStorage.getItem(GUEST_USER.IS_GUEST_USER)
            const isAuthUser = localStorage.getItem(
                AUTHENTICATED_USER.IS_LOGIN_USER
            )
            if (isGuestUser === null && isAuthUser === null) {
                const guestCheckInData = await guestCheckInEDV()
                if (guestCheckInData) {
                    Axios.setEndeavorAuthToken(guestCheckInData)
                    localStorage.setItem(
                        AUTH_TOKEN.ENDEAVOR_AUTH_TOKEN,
                        JSON.stringify(guestCheckInData)
                    )
                    localStorage.setItem(
                        GUEST_USER.IS_GUEST_USER,
                        GUEST_USER.IS_UN_AUTHENTICATED_USER
                    )
                }
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoadApp(true)
        }
    }, [])

    useEffect(() => {
        guestCheckIn().catch((error) => console.log(error))
    }, [guestCheckIn])

    useEffect(() => {
        window.addEventListener('logout', () => {
            void (async () => {
                await guestCheckIn()
            })()
        })
        return () => {
            window.removeEventListener('logout', () => {
                void (async () => {
                    await guestCheckIn()
                })()
            })
        }
    }, [guestCheckIn])

    useEffect(() => {
        // START   -------------------------------------------------------------- reCaptcha V3 Script
        const captchaScript = document.createElement('script')
        captchaScript.type = 'text/javascript'
        captchaScript.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_CAPTCHA_SITE_KEY}`
        document.body.appendChild(captchaScript)
        // END     -------------------------------------------------------------- reCaptcha V3 Script

        const script = document.createElement('script')
        script.src =
            'https://doris-embed.diceplatform.com/scripts/2/embed.min.js'
        script.async = true

        document.head.appendChild(script)

        // START   -------------------------------------------------------------- Cookie consent script
        if (
            process.env.REACT_APP_ONE_TRUST_COOKIE_CONSENT_KEY &&
            process.env.REACT_APP_ENV === 'production'
        ) {
            const cookieConsentScript = document.createElement('script')
            cookieConsentScript.src =
                'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
            cookieConsentScript.type = 'text/javascript'
            cookieConsentScript.setAttribute(
                'data-domain-script',
                process.env.REACT_APP_ONE_TRUST_COOKIE_CONSENT_KEY
            )
            document.head.insertBefore(
                cookieConsentScript,
                document.head.firstChild
            )
        }
        // END     -------------------------------------------------------------- Cookie consent script

        // To logout user from all tabs when logged out from one tab
        window.addEventListener('storage', reloadOnLogout)

        // START   -------------------------------------------------------------- Google tag manager
        if (process.env?.REACT_APP_GTM_CODE) {
            GoogleTagManager.initialize({
                gtmId: process.env.REACT_APP_GTM_CODE,
            })
        }
        // END     -------------------------------------------------------------- Google tag manager

        return () => {
            window.removeEventListener('storage', reloadOnLogout)
        }
    }, [])

    const branchController = async () => {
        return new Promise((resolve, reject) => {
            branch.init(
                process.env.REACT_APP_BRANCH_IO_PRIVATE_KEY
                    ? process.env.REACT_APP_BRANCH_IO_PRIVATE_KEY
                    : '',
                {},
                (error, data) => {
                    if (error) {
                        reject(error)
                    } else {
                        resolve(data?.data_parsed)
                    }
                }
            )
        })
    }

    useEffect(() => {
        branchController().catch((error) => console.log(error))
    }, [])

    useEffect(() => {
        const timeInterval = 14 * 24 * 60 * 60 * 1000 // 14 days in milliseconds

        const timer = setTimeout(() => {
            clearCacheAndReload()
        }, timeInterval)

        return () => {
            clearTimeout(timer)
        }
    }, [])

    return <Providers>{loadApp && <Router />}</Providers>
}

export default App
